import React from 'react'
import { useIntl } from 'react-intl'
import classNames from 'classnames'
import { NotAvailableServiceSign } from '@/components/composed/landingBlocks/deprecated/LandingHeader/NotAvailableServiceSign'
import { useLandingAccessControl } from '@/components/composed/landingBlocks/LandingAccessControlProvider'
import { ELandingAccessControlValue } from '@/components/composed/landingBlocks/LandingAccessControlProvider/types'
import { LandingDetailsOld } from '@/components/composed/landingBlocks/LandingDetailsOld'
import { Breadcrumbs, BubblingTooltip, Button, Image, MetaInfo, TemporaryUnavailableTag } from '@/components/ui'
import { homeBreadcrumb } from '@/components/ui/Breadcrumbs/constants'
import type { IHeaderProps } from './interfaces'
import './styles.scss'

const LandingHeader: React.FC<IHeaderProps> = props => {
  const {
    actionButtonText,
    titleText,
    subTitle,
    descriptionText,
    descriptionClassName,
    bannerSaleInfoTitle,
    bannerSaleInfoDescription,
    bannerSaleInfoConditionsTitle,
    bannerSaleInfoConditionsList,
    bannerSaleInfoConditionsCite,
    isDeviceMobile,
    details,
    className = '',
    titleClassName,
    backgroundClassName = '',
    buttonClassName = '',
    partnerImages = [],
    comment,
    onActionClick,
    rootBreadcrumbs,
    breadcrumbCaptionId,
    breadcrumbCaption,
    styles,
    isDisabled = false,
    buttonTooltipMessageId,
    buttonTooltipMessage,
    isBannerSale,
    serviceIsNotAvailable,
    children,
    mobileImageUrl,
    additionalActions,
  } = props

  const { formatMessage } = useIntl()

  const { resolve } = useLandingAccessControl()

  const shouldDisableButton =
    Boolean(buttonTooltipMessageId || buttonTooltipMessage) &&
    resolve('landings.components.header.button') === ELandingAccessControlValue.VISIBLE_AND_BLOCKED

  const actionButton = (
    <Button modifiers={['green', 'full-width-mobile']} onClick={onActionClick} className={buttonClassName} isDisabled={shouldDisableButton}>
      {actionButtonText}
    </Button>
  )

  return (
    <section className={classNames('landing-header', className)}>
      {isDeviceMobile && (breadcrumbCaptionId || breadcrumbCaption) && rootBreadcrumbs && (
        <div className={isDeviceMobile && 'landingHeaderBreadcrumbsMobileWrapper'}>
          <Breadcrumbs
            customBreadcrumbs={[homeBreadcrumb, ...rootBreadcrumbs, { captionId: breadcrumbCaptionId, caption: breadcrumbCaption }]}
            isMobile={isDeviceMobile}
          />
        </div>
      )}
      <div className={classNames('landing-header-background', backgroundClassName)} style={styles}>
        <div className="page-wrapper landing-header-content">
          {(breadcrumbCaptionId || breadcrumbCaption) && rootBreadcrumbs && (
            <>
              <MetaInfo metaInfo={{ title: breadcrumbCaption ?? formatMessage({ id: breadcrumbCaptionId }) }} />
              {!isDeviceMobile && (
                <Breadcrumbs
                  customBreadcrumbs={[homeBreadcrumb, ...rootBreadcrumbs, { captionId: breadcrumbCaptionId, caption: breadcrumbCaption }]}
                />
              )}
            </>
          )}
          <div className="space-holder24 space-holder-top16-sm space-holder-top24-from-sm space-holder16-sm landing-header__title">
            <h2 className={classNames('h3-only-sm', titleClassName)}>{titleText}</h2>
          </div>
          {subTitle && <div className="landing-header-banner__subtitle">{subTitle}</div>}
          {isDisabled && <TemporaryUnavailableTag className="landing-header__unavailable" />}
          {serviceIsNotAvailable ? (
            <div className="landing-header-content-not-available">
              <NotAvailableServiceSign />
            </div>
          ) : (
            <>
              <p className={classNames('landing-header__text space-holder40-from-sm space-holder24-sm', descriptionClassName)}>
                {descriptionText}
              </p>

              {partnerImages.map((name, index) => (
                <Image key={index} src={`index/partners/${name}.png`} isInStatic className="landing-header-partner" />
              ))}

              <div className="landing-header-content-actions">
                {Boolean(actionButtonText) &&
                  ((buttonTooltipMessageId || buttonTooltipMessage) &&
                  resolve('landings.components.forAgrariansOnlyTooltip') === ELandingAccessControlValue.VISIBLE ? (
                    <BubblingTooltip messageId={buttonTooltipMessageId} content={buttonTooltipMessage}>
                      <div className="landingHeaderAction__creditButton">{actionButton}</div>
                    </BubblingTooltip>
                  ) : (
                    actionButton
                  ))}

                {additionalActions}
              </div>

              {children}
            </>
          )}

          {comment && (
            <p
              className="
                landing-header__comment
                text_super-small
                color_pale-black
                space-holder-top32-from-md
                space-holder-top40-only-md
                space-holder-top24-sm
              "
            >
              {comment}
            </p>
          )}
        </div>
        {mobileImageUrl && isDeviceMobile && (
          <div className="page-wrapper">
            <Image className="landingHeaderMobileImage" src={mobileImageUrl} isInStatic />
          </div>
        )}
      </div>
      {isBannerSale && (
        <div className="landing-header-banner">
          <div className="page-wrapper landing-header-banner__wrapper">
            <div className="landing-header-banner__heading">
              <h3 className="landing-header-banner__title">{bannerSaleInfoTitle}</h3>
              <p className="text_super-small-sm">{bannerSaleInfoDescription}</p>
            </div>
            <div className="landing-header-banner__conditions">
              <h6 className="landing-header-banner__conditions-title">{bannerSaleInfoConditionsTitle}</h6>
              {bannerSaleInfoConditionsList}
              <p className="text_super-small landing-header-banner__conditions-cite">{bannerSaleInfoConditionsCite}</p>
            </div>
          </div>
        </div>
      )}
      {details && <LandingDetailsOld details={details} />}
    </section>
  )
}

export default LandingHeader
